import { usePredictionMarketCategories } from '@/lib/endpoints';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import { cn } from '@/lib/utils';

export interface CategoryBannerProps {
  className?: string;
}

/**
 * Optional wide image for the selected category — with the category title
 * overlaid on it, vertically centered at the left edge — shown at the top of
 * the feed directly under the category tab bar. Renders nothing on the default
 * Trending view (no `?category=` param), for categories without a banner,
 * and for territory-blocked categories (mirrors the filter in
 * {@link CategoryTabsContainer} — a blocked code can still arrive via URL).
 *
 * Shares the categories query cache with `CategoryTabsContainer`, so it
 * costs no extra network request.
 */
export function CategoryBanner({ className }: CategoryBannerProps) {
  const { category } = useAppSearchParams();
  const { data } = usePredictionMarketCategories();
  const hiddenCategories = useHiddenCategories();

  if (!category) return null;
  if (hiddenCategories.includes(category.toUpperCase())) return null;

  const selected = data?.find((cat) => cat.code === category);
  if (!selected?.bannerImgUrl) return null;

  return (
    // Width cap tracks the category feed below it (760px → 1300px at xl, cf. BinaryLobbyFeed).
    <div className={cn('w-full max-w-[760px] xl:max-w-[1300px] px-3.5 md:px-0', className)}>
      {/* Keyed by category so switching tabs replays the entrance. */}
      <div key={selected.code} className="relative animate-banner-in">
        <img
          src={selected.bannerImgUrl}
          alt={selected.displayName ?? selected.code}
          className="w-full rounded-card object-cover"
        />
        <h2 className="absolute left-4 top-1/2 -translate-y-1/2 m-0 text-lg font-semibold text-surface animate-fade-in [animation-delay:150ms]">
          {selected.displayName ?? selected.code}
        </h2>
      </div>
    </div>
  );
}
